import { fieldMessage, type FieldInfo } from './fieldMessage'
import { FIELD_ERROR_TEXTS } from '../../shared/texts'
import type { SaveResult } from '../layout/TabsContext'

// One way for every form (operator, 02.10.2026): no browser bubble — the form carries `noValidate`
// and calls checkForm() at the start of its save(). The rules stay where they already are, in the
// markup (`required`, `type="email"`, `min`/`max`, …); the browser's verdict (el.validity) is read
// and turned into OUR German message, written UNDER the field, the field marked, the first one
// focused. Because the check sits in save() and not in the submit handler, it also covers the
// paths that never submit a form: the close-tab guard and „Alle übernehmen".
//
// A component without a native control (SearchSelect: the value is an id, the input shows a
// label) marks its input with `data-required` and `data-filled="true|false"`.
//
// Works on any container, not only a <form> — a drawer without a form passes its root element.

export interface CheckResult {
    ok: boolean
    // The first message, with the field's name — for a dialog („Alle übernehmen", close-tab guard)
    // that cannot point at the field itself.
    message?: string
    // Whether the first invalid field can be seen (it may sit in a hidden tab or pane). When it
    // cannot, the form should show the message where it can be seen as well.
    visible?: boolean
}

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const MARK = 'data-check' // set on everything this module adds, so the next check can clear it
let seq = 0

// The field's name: its label within the .field box, without the „*" and info tips; else the
// column header of a table cell; else aria-label / title / placeholder.
export function labelOf(el: Element): string {
    const field = el.closest('.field')
    const label = field?.querySelector(':scope > label') ?? field?.querySelector('label')
    if (label) {
        const copy = label.cloneNode(true) as HTMLElement
        copy.querySelectorAll('.infotip, button, svg, input, select, textarea').forEach((n) => n.remove())
        const text = (copy.textContent ?? '').replace(/\s+/g, ' ').replace(/[\s*:]+$/, '').trim()
        if (text) return text
    }
    const cell = el.closest('td, th') as HTMLTableCellElement | null
    const table = cell?.closest('table')
    if (cell && table) {
        const head = table.querySelector('thead tr')?.children[cell.cellIndex] as HTMLElement | undefined
        const text = (head?.textContent ?? '').replace(/\s+/g, ' ').trim()
        if (text) return text
    }
    const named = el.getAttribute('aria-label') ?? el.getAttribute('title')
    if (named) return named
    // A placeholder as the last resort, without its trailing dots („Neuer Punkt…" → „Neuer Punkt").
    return (el.getAttribute('placeholder') ?? '').replace(/[\s….]+$/, '').trim()
}

function infoOf(el: Control): FieldInfo {
    const input = el instanceof HTMLInputElement ? el : null
    return {
        label: labelOf(el),
        type: input ? input.type : el.tagName.toLowerCase(),
        min: input?.min || undefined,
        max: input?.max || undefined,
        step: input?.step || undefined,
        maxLength: input && input.maxLength > 0 ? input.maxLength : undefined,
        minLength: input && input.minLength > 0 ? input.minLength : undefined,
    }
}

function isVisible(el: HTMLElement): boolean {
    return el.getClientRects().length > 0
}

// Remove every mark this module set inside `root`.
export function clearChecks(root: HTMLElement | null | undefined) {
    if (!root) return
    root.querySelectorAll(`.field-error[${MARK}]`).forEach((n) => n.remove())
    root.querySelectorAll(`[${MARK}-field]`).forEach((n) => { n.classList.remove('has-error'); n.removeAttribute(`${MARK}-field`) })
    root.querySelectorAll(`[${MARK}-invalid]`).forEach((n) => {
        n.removeAttribute('aria-invalid'); n.removeAttribute('aria-describedby'); n.removeAttribute(`${MARK}-invalid`)
    })
}

function clearOne(el: HTMLElement, box: HTMLElement, note: HTMLElement) {
    note.remove()
    box.classList.remove('has-error'); box.removeAttribute(`${MARK}-field`)
    el.removeAttribute('aria-invalid'); el.removeAttribute('aria-describedby'); el.removeAttribute(`${MARK}-invalid`)
}

function mark(el: HTMLElement, message: string) {
    const box = (el.closest('.field') as HTMLElement | null) ?? el.parentElement ?? el
    box.classList.add('has-error'); box.setAttribute(`${MARK}-field`, '')
    const note = document.createElement('small')
    note.className = 'field-error'
    note.setAttribute(MARK, '')
    note.id = `field-error-${++seq}`
    note.textContent = message
    box.appendChild(note)
    el.setAttribute('aria-invalid', 'true'); el.setAttribute('aria-describedby', note.id); el.setAttribute(`${MARK}-invalid`, '')

    // The mark goes as soon as the field is touched again (typed, picked, or — for a SearchSelect —
    // its value set).
    const done = () => { clearOne(el, box, note); el.removeEventListener('input', done); el.removeEventListener('change', done); observer?.disconnect() }
    el.addEventListener('input', done)
    el.addEventListener('change', done)
    const observer = el.hasAttribute('data-required')
        ? new MutationObserver(() => { if (el.getAttribute('data-filled') === 'true') done() })
        : null
    observer?.observe(el, { attributes: true, attributeFilter: ['data-filled'] })
}

export function checkForm(root: HTMLElement | null | undefined): CheckResult {
    if (!root) return { ok: true }
    clearChecks(root)
    const bad: { el: HTMLElement, message: string }[] = []

    root.querySelectorAll<Control>('input, select, textarea').forEach((el) => {
        // :disabled also covers a control inside a disabled <fieldset> (el.disabled stays false there).
        if (el.matches(':disabled')) return
        // A component with its own value (SearchSelect): required and not filled.
        if (el.hasAttribute('data-required')) {
            if (el.getAttribute('data-filled') !== 'true') {
                bad.push({ el, message: fieldMessage({ valueMissing: true }, { ...infoOf(el), type: 'text' }) ?? '' })
            }
            return
        }
        if (!el.willValidate || el.validity.valid) return
        const message = fieldMessage(el.validity, infoOf(el)) ?? fieldMessage({ patternMismatch: true }, infoOf(el)) ?? FIELD_ERROR_TEXTS.invalid
        bad.push({ el, message })
    })

    if (!bad.length) return { ok: true }
    for (const b of bad) mark(b.el, b.message)
    const first = bad[0].el
    const visible = isVisible(first)
    if (visible) {
        first.focus({ preventScroll: true })
        first.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    return { ok: false, message: bad[0].message, visible }
}

// For a form's save(): null when every field is fine; otherwise the failed SaveResult (its error
// names the field, for the close-tab guard and „Alle übernehmen"). The form's own notice at the
// top is used only when the field cannot be seen — otherwise the message stands at the field.
export function checkBeforeSave(root: HTMLElement | null | undefined, setError: (msg: string) => void): SaveResult | null {
    const check = checkForm(root)
    if (check.ok) return null
    setError(check.visible ? '' : check.message ?? '')
    return { ok: false, error: check.message }
}

// For a save path without a <form> (drawer, dialog, table row, add row): checks the container
// around the clicked button / the field Enter was pressed in. `selector` names that container.
export function checkWithin(e: { currentTarget: EventTarget | null } | undefined, selector: string): boolean {
    const from = e?.currentTarget as Element | null | undefined
    return checkForm((from?.closest(selector) as HTMLElement | null) ?? null).ok
}
